// frontend/src/utils/mappingIO.js
/**
 * Import / Export de Mapping — DatiaFlow
 *
 * Responsabilidades:
 *   ► Generar JSON y Excel desde localMap (wizard) o mapping array (dashboard)
 *   ► Parsear archivos JSON y Excel subidos por el usuario
 *   ► Validar columnas importadas contra el esquema actual (source/target)
 *
 * Dependencia: npm install xlsx  (SheetJS)
 */

// EXAMPLES
// Export JSON de un campo con reglas:
// {
//   "sourceField": "fecha_emision",
//   "targetField": "invoice_date",
//   "dataType": "DATE",
//   "transformations": [
//     { "id": "FORMAT_DATE", "label": "FORMAT_DATE", "params": { "from": "DD/MM/YYYY", "to": "YYYY-MM-DD" } },
//     { "id": "DEFAULT_NULL", "label": "DEFAULT_NULL", "params": { "detectType": "both", "value": "1900-01-01" } }
//   ],
//   "ignore": false
// }

// Export Excel
// Source / Target / Data Type / Transformations / Ignored
// fecha_emision / invoice_date / DATE / FORMAT_DATE(DD/MM/YYYY, YYYY-MM-DD) → DEFAULT_NULL(both, 1900-01-01)
//
// { id: 'FORMAT_DATE', params: { from: 'DD/MM/YYYY', to: 'YYYY-MM-DD' } }
// { id: 'DEFAULT_NULL', params: { detectType: 'both', value: '1900-01-01' } }

import * as XLSX from 'xlsx';
import { KNOWN_TRANSFORM_IDS, getTransformParams } from './transformRules';

// ════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ════════════════════════════════════════════════════════════════════════

const EXPORT_VERSION = '1.0';

// Headers canónicos — validación estricta
const REQUIRED_JSON_KEYS = new Set(['sourceField', 'targetField']);
const VALID_JSON_KEYS = new Set(['sourceField', 'targetField', 'dataType', 'transformations', 'ignore']);

const REQUIRED_EXCEL_HEADERS = new Set(['Source', 'Target']);
const VALID_EXCEL_HEADERS = new Set(['Source', 'Target', 'Data Type', 'Transformations', 'Ignored']);


// ════════════════════════════════════════════════════════════════════════
// HELPERS DE SERIALIZACIÓN (Excel ↔ transformaciones)
// ════════════════════════════════════════════════════════════════════════

/**
 * Serializa una transformación a string legible para Excel.
 *
 * Sin params:   TRIM_EDGES
 * Con params:   SET_FIXED(100)
 *               REPLACE(viejo, nuevo)
 *               FORMAT_DATE(YYYY-MM-DD, DD/MM/YYYY)
 *               ADVANCED_COND(EQUALS, Casa, Activo, KEEP)
 *
 * Solo incluye params con valor — no serializa keys vacíos/null.
 */
function _serializeTransform(t) {
  const id = t.id || t.label || '';
  const params = t.params || {};
  const paramDefs = getTransformParams(id);

  if (paramDefs.length === 0) return id;

  const vals = paramDefs
    .map(key => {
      const v = params[key];
      if (v === '' || v === null || v === undefined) return null;
      // Objetos/arrays → serializar como JSON para roundtrip correcto
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    })
    .filter(v => v !== null);

  return vals.length > 0 ? `${id}(${vals.join(', ')})` : id;
}

/**
 * Divide params respetando {} [] "" — no rompe JSON embebido.
 * "viejo, nuevo"                     → ["viejo", "nuevo"]
 * '{"M":"Masculino","F":"Fem"}, N/A' → ['{"M":"Masculino","F":"Fem"}', 'N/A']
 */
function _smartSplitParams(raw) {
  const parts = [];
  let current = '';
  let depth = 0;       // {} / []
  let inQuote = false;
  let quoteChar = '';

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (inQuote) {
      current += ch;
      if (ch === quoteChar && raw[i - 1] !== '\\') inQuote = false;
      continue;
    }

    if (ch === '"' || ch === "'") {
      inQuote = true;
      quoteChar = ch;
      current += ch;
      continue;
    }

    if (ch === '{' || ch === '[') { depth++; current += ch; continue; }
    if (ch === '}' || ch === ']') { depth--; current += ch; continue; }

    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

/**
 * Parsea string de transformación de Excel a objeto.
 *
 * "SET_FIXED(100)"                      → { id: 'SET_FIXED', params: { value: '100' } }
 * "SET_FIXED(Lima, Perú)"               → { id: 'SET_FIXED', params: { value: 'Lima, Perú' } }
 * "REPLACE(old, new)"                   → { id: 'REPLACE', params: { search: 'old', replace: 'new' } }
 * "FORMAT_DATE(YYYY-MM-DD, DD/MM/YYYY)" → { id: 'FORMAT_DATE', params: { from: '...', to: '...' } }
 * "TRANSLATE_VALUE({...JSON...}, N/A)"  → { id: '...', params: { mappings: {...}, default: 'N/A' } }
 * "TRIM_EDGES"                          → { id: 'TRIM_EDGES', params: {} }
 */
function _deserializeTransform(str) {
  const match = str.match(/^([A-Z_][A-Z0-9_]*)\((.+)\)$/);
  if (match) {
    const id = match[1];
    const rawParams = match[2];
    const paramNames = getTransformParams(id);

    const paramValues = paramNames.length === 1
      ? [rawParams.trim()]
      : _smartSplitParams(rawParams);

    const params = {};
    paramValues.forEach((val, i) => {
      const key = paramNames[i] || `p${i}`;
      if ((val.startsWith('{') && val.endsWith('}')) || (val.startsWith('[') && val.endsWith(']'))) {
        try { params[key] = JSON.parse(val); return; } catch { /* string normal */ }
      }
      params[key] = val;
    });

    // ── Sanitización de LOOKUP params contra SQL injection ──
    if (id === 'LOOKUP') {
      const SQL_IDENT = /^[a-zA-Z_][a-zA-Z0-9_.]*$/;
      ['lookupTable', 'lookupField', 'returnField', 'filterField'].forEach(key => {
        if (params[key] && !SQL_IDENT.test(params[key])) {
          params[key] = '';
        }
      });
    }

    return { id, label: id, params };
  }
  return { id: str.trim(), label: str.trim(), params: {} };
}


// ════════════════════════════════════════════════════════════════════════
// EXPORT — JSON
// ════════════════════════════════════════════════════════════════════════

/**
 * Construye el objeto canónico de mapping exportable.
 *
 * @param {Object}   localMap        — { [targetCol]: { source, type, pipeline, confidence } }
 * @param {string}   mode            — '1:1' | 'N:1' | '1:N' | 'N:M'
 * @param {string}   pipelineName    — nombre del pipeline (para metadata)
 * @param {string[]} targetTables    — tablas destino (para N:M agrupado)
 * @param {Array}    targetColumns   — columnas destino con metadata (type, nullable, etc.)
 * @returns {Object} — JSON exportable
 */
export function buildExportPayload(localMap, mode, pipelineName, targetTables, targetColumns) {
  const isMultiTable = mode !== '1:1' && targetTables?.length > 1;

  const buildEntry = (targetField, data, colMeta) => {
    const isIgnored = data.type === 'ignore';
    return {
      sourceField: isIgnored ? '' : (data.source || ''),
      targetField,
      dataType: colMeta?.type || 'VARCHAR',
      transformations: isIgnored ? [] : (data.pipeline || []).map(t => ({
        id: t.id,
        label: t.label || t.id,
        params: t.params || {},
      })),
      ignore: isIgnored,
    };
  };

  let mappings;

  if (isMultiTable) {
    mappings = {};
    targetTables.forEach(table => { mappings[table] = []; });

    Object.entries(localMap).forEach(([targetCol, data]) => {
      if (data.type === 'unmapped') return;
      const dotIdx = targetCol.indexOf('.');
      const table = dotIdx > -1 ? targetCol.substring(0, dotIdx) : targetTables[0] || '_default';
      const colName = dotIdx > -1 ? targetCol.substring(dotIdx + 1) : targetCol;
      const colMeta = targetColumns?.find(tc => tc.name === targetCol);

      if (!mappings[table]) mappings[table] = [];
      mappings[table].push(buildEntry(colName, data, colMeta));
    });
  } else {
    mappings = Object.entries(localMap)
      .filter(([, data]) => data.type !== 'unmapped')
      .map(([targetCol, data]) => {
        const colMeta = targetColumns?.find(tc => tc.name === targetCol);
        return buildEntry(targetCol, data, colMeta);
      });
  }

  return {
    version: EXPORT_VERSION,
    mode: isMultiTable ? 'N:M' : '1:1',
    exportedAt: Date.now(),
    pipelineName: pipelineName || '',
    mappings,
  };
}


/**
 * Construye el payload desde un mapping array ya guardado (para Dashboard export).
 *
 * @param {Array}  mappingArray  — [{ source, target, type, pipeline, confidence }]
 * @param {string} pipelineName
 * @param {string} mode
 * @returns {Object}
 */

export function buildExportFromSaved(mappingArray, pipelineName, mode) {
  const isNM = mode === 'N:M';

  const buildEntry = (m) => {
    const isIgnored = m.type === 'ignore' || m.source === 'IGNORE';
    const target = m.target || '';
    // En N:M, quitar el prefijo "tabla." del target para la entrada
    const dotIdx = target.indexOf('.');
    const cleanTarget = isNM && dotIdx > -1 ? target.substring(dotIdx + 1) : target;

    return {
      sourceField: isIgnored ? '' : (m.source || ''),
      targetField: cleanTarget,
      dataType: m.dataType || 'VARCHAR',
      transformations: isIgnored ? [] : (m.pipeline || []).map(t => ({
        id: t.id,
        label: t.label || t.id,
        params: t.params || {},
      })),
      ignore: isIgnored,
      _table: isNM && dotIdx > -1 ? target.substring(0, dotIdx) : null,
    };
  };

  const entries = (mappingArray || [])
    .filter(m => m.type !== 'unmapped')
    .map(buildEntry);

  let mappings;

  if (isNM) {
    // Agrupar por tabla destino → objeto { tabla: [...entries] }
    mappings = {};
    entries.forEach(e => {
      const table = e._table || '_default';
      if (!mappings[table]) mappings[table] = [];
      const { _table, ...cleanEntry } = e;
      mappings[table].push(cleanEntry);
    });
  } else {
    mappings = entries.map(({ _table, ...rest }) => rest);
  }

  return {
    version: EXPORT_VERSION,
    mode: isNM ? 'N:M' : '1:1',
    exportedAt: Date.now(),
    pipelineName: pipelineName || '',
    mappings,
  };
}


/**
 * Descarga un objeto como archivo JSON.
 */
export function downloadJSON(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  _triggerDownload(blob, filename.endsWith('.json') ? filename : `${filename}.json`);
}


// ════════════════════════════════════════════════════════════════════════
// EXPORT — EXCEL
// ════════════════════════════════════════════════════════════════════════

/**
 * Genera y descarga un archivo Excel desde el payload de mapping.
 *
 * 1:1 → una hoja "Mapping"
 * N:M → una hoja por tabla destino
 */
export function downloadExcel(payload, filename) {
  const wb = XLSX.utils.book_new();

  const headers = ['Source', 'Target', 'Data Type', 'Transformations', 'Ignored'];

  const buildSheet = (entries) => {
    const rows = entries.map(e => ([
      e.sourceField,
      e.targetField,
      e.dataType || '',
      e.transformations?.length
        ? e.transformations.map(t => _serializeTransform(t)).join(' | ')
        : '',
      e.ignore ? 'YES' : '',
    ]));
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    ws['!cols'] = [
      { wch: 30 }, // Source
      { wch: 30 }, // Target
      { wch: 15 }, // Data Type
      { wch: 40 }, // Transformations
      { wch: 10 }, // Ignored
    ];
    return ws;
  };

  if (payload.mode === 'N:M' && typeof payload.mappings === 'object' && !Array.isArray(payload.mappings)) {
    Object.entries(payload.mappings).forEach(([tableName, entries]) => {
      const sheetName = tableName.length > 31 ? tableName.substring(0, 31) : tableName;
      XLSX.utils.book_append_sheet(wb, buildSheet(entries), sheetName);
    });
  } else {
    const entries = Array.isArray(payload.mappings) ? payload.mappings : [];
    XLSX.utils.book_append_sheet(wb, buildSheet(entries), 'Mapping');
  }

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  _triggerDownload(blob, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}


// ════════════════════════════════════════════════════════════════════════
// IMPORT — Parsear archivos subidos
// ════════════════════════════════════════════════════════════════════════

/**
 * Lee y parsea un archivo de mapping (JSON o Excel).
 *
 * @param {File} file — archivo del input[type=file]
 * @returns {Promise<{ entries, mode, error?, invalidHeaders? }>}
 */
export async function parseImportFile(file) {
  const ext = (file.name || '').split('.').pop().toLowerCase();

  if (ext === 'json') {
    return _parseJSON(file);
  } else if (ext === 'xlsx' || ext === 'xls') {
    return _parseExcel(file);
  } else {
    return { entries: [], mode: '1:1', error: 'UNSUPPORTED_FORMAT' };
  }
}

async function _parseJSON(file) {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    if (!data.mappings) {
      return { entries: [], mode: '1:1', error: 'INVALID_STRUCTURE' };
    }

    // ── Validar headers/keys estricto ──────────────────────────────
    const sampleEntries = Array.isArray(data.mappings)
      ? data.mappings
      : Object.values(data.mappings).flat();

    if (sampleEntries.length === 0) {
      return { entries: [], mode: '1:1', error: 'EMPTY_MAPPINGS' };
    }

    const firstEntry = sampleEntries[0];
    const entryKeys = new Set(Object.keys(firstEntry));

    for (const reqKey of REQUIRED_JSON_KEYS) {
      if (!entryKeys.has(reqKey)) {
        return {
          entries: [], mode: '1:1', error: 'INVALID_HEADERS',
          invalidHeaders: { missing: reqKey, found: [...entryKeys] },
        };
      }
    }

    const unknownKeys = [...entryKeys].filter(k => !VALID_JSON_KEYS.has(k));
    if (unknownKeys.length > 0) {
      return {
        entries: [], mode: '1:1', error: 'INVALID_HEADERS',
        invalidHeaders: { unknown: unknownKeys, expected: [...VALID_JSON_KEYS] },
      };
    }

    let entries = [];
    const mode = data.mode || '1:1';

    if (mode === 'N:M' && typeof data.mappings === 'object' && !Array.isArray(data.mappings)) {
      Object.entries(data.mappings).forEach(([tableName, tableEntries]) => {
        (tableEntries || []).forEach(e => {
          entries.push({
            sourceField: e.sourceField || '',
            targetField: `${tableName}.${e.targetField}`,
            transformations: e.transformations || [],
            ignore: !!e.ignore,
            dataType: e.dataType || '',
          });
        });
      });
    } else if (Array.isArray(data.mappings)) {
      entries = data.mappings.map(e => ({
        sourceField: e.sourceField || '',
        targetField: e.targetField || '',
        transformations: e.transformations || [],
        ignore: !!e.ignore || (e.sourceField || '').toUpperCase() === 'IGNORE',
        dataType: e.dataType || '',
      }));
    }

    return { entries, mode };
  } catch {
    return { entries: [], mode: '1:1', error: 'PARSE_ERROR' };
  }
}

async function _parseExcel(file) {
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });

    if (wb.SheetNames.length === 0) {
      return { entries: [], mode: '1:1', error: 'EMPTY_MAPPINGS' };
    }

    const firstSheet = wb.Sheets[wb.SheetNames[0]];
    const firstRows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

    if (firstRows.length === 0) {
      return { entries: [], mode: '1:1', error: 'EMPTY_MAPPINGS' };
    }

    const foundHeaders = new Set(Object.keys(firstRows[0]));

    for (const reqHeader of REQUIRED_EXCEL_HEADERS) {
      if (!foundHeaders.has(reqHeader)) {
        return {
          entries: [], mode: '1:1', error: 'INVALID_HEADERS',
          invalidHeaders: { missing: reqHeader, found: [...foundHeaders], expected: [...VALID_EXCEL_HEADERS] },
        };
      }
    }

    // const unknownHeaders = [...foundHeaders].filter(h => !VALID_EXCEL_HEADERS.has(h));
    // if (unknownHeaders.length > 0) {
    //   return {
    //     entries: [], mode: '1:1', error: 'INVALID_HEADERS',
    //     invalidHeaders: { unknown: unknownHeaders, expected: [...VALID_EXCEL_HEADERS] },
    //   };
    // }
    // Headers desconocidos: WARN, no bloquear.
    // El usuario puede tener columnas extra (notas, comentarios) — ignorarlas.
    const unknownHeaders = [...foundHeaders].filter(h => !VALID_EXCEL_HEADERS.has(h));
    // Solo bloquear si FALTAN required, no si SOBRAN extras
    // (unknownHeaders se reporta como info, no como error)

    const entries = [];
    const isMultiSheet = wb.SheetNames.length > 1;

    // ── Hojas de referencia/catálogo del template oficial: ignorar ────
    const CATALOG_SHEET_PREFIXES = ['ES ', 'EN ', 'PT ', 'Template'];
    const CATALOG_SHEET_NAMES = new Set(['ES — Reglas', 'EN — Rules', 'PT — Regras', 'Template']);

    const dataSheets = wb.SheetNames.filter(name => {
      if (CATALOG_SHEET_NAMES.has(name)) return false;
      if (CATALOG_SHEET_PREFIXES.some(p => name.startsWith(p))) return false;
      return true;
    });

    // Si todas las hojas son de catálogo, caer back a primera hoja (usuario subió solo Template)
    const sheetsToProcess = dataSheets.length > 0 ? dataSheets : [wb.SheetNames[0]];
    const isMultiData = sheetsToProcess.length > 1;

    sheetsToProcess.forEach(sheetName => {
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Validar que esta hoja tenga las headers requeridas
      if (rows.length === 0) return;
      const sheetHeaders = new Set(Object.keys(rows[0]));
      const hasRequired = [...REQUIRED_EXCEL_HEADERS].every(h => sheetHeaders.has(h));
      if (!hasRequired) return; // Skip hojas sin headers de mapping

      rows.forEach(row => {
        const src = (row['Source'] || '').toString().trim();
        const tgt = (row['Target'] || '').toString().trim();
        const dtype = (row['Data Type'] || '').toString().trim();
        const trans = (row['Transformations'] || '').toString().trim();
        const ign = (row['Ignored'] || '').toString().trim();

        if (!tgt) return;

        const fullTarget = isMultiData && sheetName !== 'Mapping'
          ? `${sheetName}.${tgt}`
          : tgt;

        const pipeline = trans
          ? trans.split(/[|→]/).map(s => s.trim()).filter(Boolean).map(s => _deserializeTransform(s))
          : [];

        const isIgnored = ign.toUpperCase() === 'YES' || ign === '1' || ign.toLowerCase() === 'true'
          || src.toUpperCase() === 'IGNORE';

        entries.push({
          sourceField: src,
          targetField: fullTarget,
          transformations: pipeline,
          ignore: isIgnored,
          dataType: dtype,
        });
      });
    });

    const mode = isMultiData ? 'N:M' : '1:1';
    return { entries, mode };
  } catch {
    return { entries: [], mode: '1:1', error: 'PARSE_ERROR' };
  }
}


// ════════════════════════════════════════════════════════════════════════
// VALIDACIÓN — Cruzar import contra columnas actuales
// ════════════════════════════════════════════════════════════════════════

/**
 * Valida las entradas importadas contra las columnas reales del pipeline actual.
 */
export function validateImportedMapping(entries, sourceColumns, targetColumns) {
  const srcNames = new Set((sourceColumns || []).map(c => c.name));
  const tgtNames = new Set((targetColumns || []).map(c => c.name));

  const matched = [];
  const unmatchedSource = [];
  const unmatchedTarget = [];

  entries.forEach(entry => {
    const tgtExists = tgtNames.has(entry.targetField);

    if (!tgtExists) {
      unmatchedTarget.push(entry);
      return;
    }

    if (entry.ignore) {
      matched.push({ ...entry, status: 'ignored' });
      return;
    }

    const srcExists = srcNames.has(entry.sourceField);
    if (srcExists) {
      const tgtCol = (targetColumns || []).find(c => c.name === entry.targetField);
      const importedType = (entry.dataType || '').toUpperCase().trim();
      const actualType = (tgtCol?.type || '').toUpperCase().trim();
      const typeWarning = importedType && actualType && importedType !== actualType;

      const unknownTransforms = [];
      const paramWarnings = [];

      (entry.transformations || []).forEach(t => {
        if (!t.id) return;
        if (!KNOWN_TRANSFORM_IDS.has(t.id)) {
          unknownTransforms.push(t.id);
          return;
        }
        // Validar cantidad de params
        const expectedParams = getTransformParams(t.id);
        if (expectedParams.length > 0) {
          const providedParams = Object.keys(t.params || {}).filter(k => !k.startsWith('p'));
          const missingParams = expectedParams.filter(p =>
            t.params?.[p] === undefined || t.params?.[p] === ''
          );
          if (missingParams.length > 0) {
            paramWarnings.push({
              ruleId: t.id,
              missing: missingParams,
              expected: expectedParams,
            });
          }
        }
      });

      matched.push({
        ...entry,
        status: 'matched',
        typeWarning: typeWarning || false,
        actualType: actualType,
        unknownTransforms,
        paramWarnings,
      });
    } else {
      // Source no encontrado: incluir como matched con warning, no descartar
      matched.push({
        ...entry,
        status: 'source_warning',
        sourceWarning: true,
        typeWarning: false,
        actualType: '',
        unknownTransforms: (entry.transformations || [])
          .filter(t => t.id && !KNOWN_TRANSFORM_IDS.has(t.id))
          .map(t => t.id),
        paramWarnings: [],
      });
      unmatchedSource.push(entry); // mantener para el summary
    }
  });

  const entriesWithUnknownRules = matched.filter(e => e.unknownTransforms?.length > 0);

  return {
    matched,
    unmatchedSource,
    unmatchedTarget,
    summary: {
      total: entries.length,
      matched: matched.length,
      noSource: unmatchedSource.length,
      noTarget: unmatchedTarget.length,
      unknownRules: entriesWithUnknownRules.length,
    },
  };
}


/**
 * Aplica el mapping importado (validado) al localMap existente.
 */
export function applyImportToLocalMap(currentLocalMap, matchedEntries) {
  const newMap = { ...currentLocalMap };

  matchedEntries.forEach(entry => {
    const targetCol = entry.targetField;
    if (!newMap[targetCol]) return;
    // Si es source_warning, aplicar igual pero el source queda para revisión manual

    if (entry.status === 'ignored' || entry.ignore) {
      newMap[targetCol] = {
        ...newMap[targetCol],
        source: 'IGNORE',
        type: 'ignore',
        pipeline: [],
        confidence: null,
      };
    } else {
      newMap[targetCol] = {
        ...newMap[targetCol],
        source: entry.sourceField,
        type: entry.transformations?.length > 0 ? 'transform' : 'direct',
        pipeline: entry.transformations || [],
        confidence: null,
      };
    }
  });

  return newMap;
}


// ════════════════════════════════════════════════════════════════════════
// HELPER PRIVADO
// ════════════════════════════════════════════════════════════════════════

function _triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
